import type {
  PatientSearchProperties,
  ProfileFilterState,
  SearchPatientsParams,
} from '../../types';

export const selectAllProfiles = (): ProfileFilterState => ({
  profileFilterMode: 'exclude',
  excludeOrganizationProfileIds: [],
});

export const selectNoProfiles = (): ProfileFilterState => ({
  profileFilterMode: 'include',
  organizationProfileIds: [],
});

const addUniqueId = (ids: string[], id: string) => {
  if (ids.includes(id)) return ids;
  return [...ids, id];
};

const removeId = (ids: string[], id: string) =>
  ids.filter((currentId) => currentId !== id);

export const getIsProfileSelected = (
  filter: ProfileFilterState,
  profileId: string,
) => {
  if (filter.profileFilterMode === 'exclude') {
    return !filter.excludeOrganizationProfileIds.includes(profileId);
  }

  return filter.organizationProfileIds.includes(profileId);
};

export const getNextProfileFilter = (
  filter: ProfileFilterState,
  profileId: string,
  checked: boolean,
): ProfileFilterState => {
  if (filter.profileFilterMode === 'exclude') {
    return {
      profileFilterMode: 'exclude',
      excludeOrganizationProfileIds: checked
        ? removeId(filter.excludeOrganizationProfileIds, profileId)
        : addUniqueId(filter.excludeOrganizationProfileIds, profileId),
    };
  }

  return {
    profileFilterMode: 'include',
    organizationProfileIds: checked
      ? addUniqueId(filter.organizationProfileIds, profileId)
      : removeId(filter.organizationProfileIds, profileId),
  };
};

export const getProfileFilterSearchParams = (
  filter: ProfileFilterState,
): Pick<
  SearchPatientsParams['search'],
  'excludeOrganizationProfileIds' | 'organizationProfileIds'
> => {
  if (filter.profileFilterMode === 'exclude') {
    return {
      excludeOrganizationProfileIds: filter.excludeOrganizationProfileIds,
    };
  }

  return {
    organizationProfileIds: filter.organizationProfileIds,
  };
};

export const getHasActiveProfileFilter = (
  filter: ProfileFilterState,
  totalProfileCount: number,
) => {
  if (filter.profileFilterMode === 'exclude') {
    return filter.excludeOrganizationProfileIds.length > 0;
  }

  if (totalProfileCount === 0) {
    return filter.organizationProfileIds.length > 0;
  }

  // totalProfileCount may lag if a profile is added mid-session, causing a brief false positive
  return filter.organizationProfileIds.length !== totalProfileCount;
};

export const applyProfileFilter = (
  search: PatientSearchProperties,
  filter: ProfileFilterState,
): PatientSearchProperties => ({
  terms: search.terms,
  dateOfBirth: search.dateOfBirth,
  statuses: search.statuses,
  modifiedFrom: search.modifiedFrom,
  modifiedTo: search.modifiedTo,
  ...filter,
});
